import { useEffect, useState } from "react";
import { logger } from "../lib/logger";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, BarChart3, ChevronRight, FileText, Flame, Gift, Microscope, Sparkles, Type, User, Users, Zap,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AppShell, PageLoader } from "../components/AppShell";
import { CopyBtn } from "../components/CopyBtn";
import { api } from "../lib/api";
import { tplIcons } from "../components/icons";

export default function DashboardPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clone, setClone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/user/stats"),
          api.get("/clone").catch(() => ({ data: null })),
        ]);
        setStats(s.data);
        setClone(c?.data || null);
      } catch (err) {
        logger.error("Failed to load dashboard stats", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <PageLoader />;

  const statCards = [
    { l: "Credits", v: stats?.credits ?? 0, i: Zap, c: "text-brand" },
    { l: "Generations", v: stats?.total_generations ?? 0, i: Sparkles, c: "text-purple-600" },
    { l: "Words", v: (stats?.total_words ?? 0).toLocaleString(), i: Type, c: "text-green-600" },
    { l: "Referrals", v: stats?.referral_count ?? 0, i: Users, c: "text-orange-600" },
  ];

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8" data-testid="dashboard-page">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-3xl font-black tracking-tighter text-zinc-950 mb-1 font-heading">
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-zinc-500">Your content hub.</p>
        </div>

        {/* Signature features strip — the reason creators stay */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="signature-features">
          <button
            onClick={() => nav("/clone")}
            data-testid="feature-clone"
            className={`text-left p-5 border transition-all group ${clone?.active ? "bg-brand text-white border-brand" : "bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 hover:border-brand"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <User className="w-4 h-4" />
              <span className="text-[10px] font-bold tracking-wider uppercase">
                {clone?.active ? "Active · writing as you" : clone ? "Paused" : "Not trained"}
              </span>
            </div>
            <h3 className="text-lg font-black tracking-tight">My Clone</h3>
            <p className={`text-xs mt-1 ${clone?.active ? "text-white/80" : "text-zinc-500"}`}>
              {clone ? "Voice DNA locked in." : "Train it in ~30 seconds."}
            </p>
          </button>
          <button
            onClick={() => nav("/trends")}
            data-testid="feature-trends"
            className="text-left p-5 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 hover:border-orange-500 transition-all"
          >
            <div className="flex items-center gap-2 mb-1 text-orange-500">
              <Flame className="w-4 h-4" />
              <span className="text-[10px] font-bold tracking-wider uppercase">Live</span>
            </div>
            <h3 className="text-lg font-black tracking-tight text-zinc-950 dark:text-white">Trend Radar</h3>
            <p className="text-xs mt-1 text-zinc-500">What's breaking this week + today's ideas.</p>
          </button>
          <button
            onClick={() => nav("/autopsy")}
            data-testid="feature-autopsy"
            className="text-left p-5 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 hover:border-purple-500 transition-all"
          >
            <div className="flex items-center gap-2 mb-1 text-purple-500">
              <Microscope className="w-4 h-4" />
              <span className="text-[10px] font-bold tracking-wider uppercase">Reverse-engineer</span>
            </div>
            <h3 className="text-lg font-black tracking-tight text-zinc-950 dark:text-white">Viral Autopsy</h3>
            <p className="text-xs mt-1 text-zinc-500">Paste any viral post. Get the template.</p>
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map(({ l, v, i: Icon, c }, idx) => (
            <div key={l} className="bg-white border border-zinc-200 p-5 animate-fade-in" style={{ animationDelay: `${idx * 0.05}s` }} data-testid={`stat-${l.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold tracking-[0.15em] text-zinc-400 uppercase">{l}</p>
                <Icon className={`w-4 h-4 ${c}`} />
              </div>
              <div className="text-3xl font-black tracking-tighter text-zinc-950 font-heading">{v}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-zinc-950 p-6 animate-fade-in" data-testid="generate-cta">
            <Sparkles className="w-8 h-8 text-brand mb-4" />
            <h3 className="text-xl font-bold text-white mb-2 font-heading">Generate Content</h3>
            <p className="text-zinc-400 text-sm mb-6">Now with length control & AI Refiner.</p>
            <button data-testid="generate-now-btn" onClick={() => nav("/generate")} className="bg-brand text-white font-semibold px-6 py-2.5 hover:bg-brand-hover flex items-center gap-2">
              Generate <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="bg-white border border-zinc-200 p-6 animate-fade-in" data-testid="referral-card">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-5 h-5 text-brand" />
              <h3 className="text-lg font-bold text-zinc-900 font-heading">Invite & Earn</h3>
            </div>
            <p className="text-sm text-zinc-500 mb-4">Both get 5 free credits.</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-zinc-100 border border-zinc-200 px-3 py-2.5 text-sm font-mono font-bold text-zinc-900 tracking-wider" data-testid="referral-code-display">
                {stats?.referral_code || "..."}
              </code>
              <CopyBtn text={stats?.referral_code || ""} />
            </div>
          </div>
          <div className="bg-white border border-zinc-200 p-6 animate-fade-in" data-testid="template-breakdown">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-brand" />
              <h3 className="text-lg font-bold text-zinc-900 font-heading">Usage</h3>
            </div>
            {stats?.template_breakdown?.length > 0 ? (
              <div className="space-y-3">
                {stats.template_breakdown.map((t) => {
                  const mx = Math.max(...stats.template_breakdown.map((b) => b.count));
                  return (
                    <div key={t.name}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-zinc-700 truncate">{t.name}</span>
                        <span className="text-zinc-400 text-xs">{t.count}</span>
                      </div>
                      <div className="h-1.5 bg-zinc-100 overflow-hidden">
                        <div className="h-full bg-brand transition-all duration-700" style={{ width: `${(t.count / mx) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-zinc-400">No generations yet.</p>
            )}
          </div>
        </div>
        {stats?.recent_generations?.length > 0 && (
          <div className="mt-8 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-zinc-900 font-heading">Recent</h2>
              <button data-testid="view-all-history-btn" onClick={() => nav("/history")} className="text-sm text-brand font-semibold hover:underline flex items-center gap-1">
                View all <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              {stats.recent_generations.map((g) => {
                const Icon = tplIcons[g.template] || FileText;
                return (
                  <div key={g.id} className="bg-white border border-zinc-200 p-4 flex items-center gap-4 hover:border-zinc-300" data-testid={`recent-gen-${g.id}`}>
                    <div className="w-10 h-10 bg-brand/5 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-brand" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-zinc-900 text-sm truncate">{g.topic}</p>
                      <p className="text-xs text-zinc-500">{g.template_name} &middot; {g.word_count || 0} words</p>
                    </div>
                    <span className="text-xs text-zinc-400 flex-shrink-0">{new Date(g.created_at).toLocaleDateString()}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
